import { t } from "@/lib/i18n/interpolate";
import {
  formatSimplified,
  type HeronStep,
  heronSteps,
  type PowerBounds,
  powerBounds,
  radicalSign,
  type SimplifiedRadical,
  simplifyNthRoot,
} from "@/lib/tool-kit/arithmetic/radicals";
import { type RootDigits, rootToPlaces } from "@/lib/tool-kit/arithmetic/roots";
import {
  absBig,
  BIG_ONE,
  BIG_ZERO,
  formatBig,
} from "@/lib/tool-kit/math/bigint";
import {
  exponentForm,
  factorize,
  MAX_FACTOR_INPUT,
} from "@/lib/tool-kit/math/primes";
import { parseRational, type Rational } from "@/lib/tool-kit/math/rational";

/**
 * Everything the root calculator shows: the decimal root to the chosen places, the
 * simplest radical form, whether the radicand is a perfect power, the perfect powers on
 * either side, and (for square roots) Heron's method.
 */
export type RootKind = "real" | "imaginary" | "none";

export interface RootView {
  x: Rational;
  xText: string;
  index: number;
  places: number;
  kind: RootKind;
  /** The principal root's digits for |x|, rounded half up. */
  digits: RootDigits | null;
  radical: SimplifiedRadical | null;
  radicalText: string | null;
  /** The radicand is a perfect n-th power of a rational number. */
  perfect: boolean;
  /** The exact rational root, when perfect. */
  exactRoot: Rational | null;
  bounds: PowerBounds | null;
  heron: HeronStep[] | null;
  /** The integer radicand's prime factorisation as text, if it can be shown. */
  factorization: string | null;
  negative: boolean;
}

export type RootComputation =
  | { kind: "empty" | "invalid-number" | "invalid-index" | "too-big" }
  | { kind: "ok"; view: RootView };

export const MAX_INDEX = 100;
const MAX_DIGITS_WORK = 30_000;
const HERON_LIMIT = 1e15;
const HERON_STEPS = 5;
const INTEGER_TEXT = /^[+-]?\d+$/;

function indexOf(raw: string): number | null {
  const text = raw.trim();
  if (!INTEGER_TEXT.test(text)) {
    return null;
  }
  const n = Number.parseInt(text, 10);
  return n >= 1 && n <= MAX_INDEX ? n : null;
}

function workTooBig(
  magnitude: Rational,
  index: number,
  places: number
): boolean {
  const size =
    magnitude.num.toString().length + magnitude.den.toString().length;
  return size + index * places > MAX_DIGITS_WORK;
}

function factorLineFor(magnitude: Rational): string | null {
  const { num, den } = magnitude;
  if (den !== BIG_ONE || num < BigInt(4) || num > MAX_FACTOR_INPUT) {
    return null;
  }
  const { factors } = factorize(num);
  return exponentForm(factors);
}

function kindFor(negative: boolean, index: number): RootKind {
  if (!negative || index % 2 === 1) {
    return "real";
  }
  return index === 2 ? "imaginary" : "none";
}

function isPerfect(zero: boolean, radical: SimplifiedRadical | null): boolean {
  if (zero) {
    return true;
  }
  return radical?.complete === true && radical.radicand === BIG_ONE;
}

function heronFor(magnitude: Rational, index: number): HeronStep[] | null {
  const numeric = Number(magnitude.num) / Number(magnitude.den);
  if (index !== 2 || !(numeric > 0 && numeric < HERON_LIMIT)) {
    return null;
  }
  return heronSteps(numeric, HERON_STEPS);
}

function buildView(
  x: Rational,
  xText: string,
  index: number,
  places: number
): RootView {
  const negative = x.num < BIG_ZERO;
  const magnitude: Rational = { num: absBig(x.num), den: x.den };
  const kind = kindFor(negative, index);
  const real = kind !== "none";
  const zero = magnitude.num === BIG_ZERO;
  const digits = real
    ? rootToPlaces(magnitude.num, magnitude.den, index, places)
    : null;
  const radical = real && !zero ? simplifyNthRoot(magnitude, index) : null;
  const perfect = real && isPerfect(zero, radical);
  const whole = magnitude.num / magnitude.den;
  const showBounds = real && !perfect && whole >= BIG_ONE;
  return {
    x,
    xText,
    index,
    places,
    kind,
    digits,
    radical,
    radicalText: radical && !perfect ? formatSimplified(radical, index) : null,
    perfect,
    exactRoot: perfect && radical ? radical.coefficient : null,
    bounds: showBounds ? powerBounds(whole, index) : null,
    heron: real && !perfect && !negative ? heronFor(magnitude, index) : null,
    factorization: real ? factorLineFor(magnitude) : null,
    negative,
  };
}

export function computeRoot(
  rawX: string,
  rawIndex: string,
  places: number
): RootComputation {
  if (rawX.trim() === "" || rawIndex.trim() === "") {
    return { kind: "empty" };
  }
  const x = parseRational(rawX);
  if (x === null) {
    return { kind: "invalid-number" };
  }
  const index = indexOf(rawIndex);
  if (index === null) {
    return { kind: "invalid-index" };
  }
  if (workTooBig({ num: absBig(x.num), den: x.den }, index, places)) {
    return { kind: "too-big" };
  }
  return {
    kind: "ok",
    view: buildView(x, rawX.trim().replace("-", "−"), index, places),
  };
}

/* ------------------------------------------------------------ narration */

export interface RootWords {
  factorLine: string;
  pullOutSquare: string;
  pullOutIndex: string;
  nothingOut: string;
  radicalLine: string;
  perfectLine: string;
}

/** The working in sentences. */
export function rootWorking(view: RootView, words: RootWords): string[] {
  const lines: string[] = [];
  const sign = radicalSign(view.index);
  const abs = view.xText.replace("−", "");
  if (view.kind === "none" || view.radical === null) {
    return lines;
  }
  if (view.factorization) {
    lines.push(t(words.factorLine, { x: abs, factors: view.factorization }));
  }
  if (view.perfect && view.exactRoot) {
    const root =
      view.exactRoot.den === BIG_ONE
        ? formatBig(view.exactRoot.num)
        : `${view.exactRoot.num}/${view.exactRoot.den}`;
    lines.push(t(words.perfectLine, { sign, x: abs, root }));
    return lines;
  }
  if (view.radicalText) {
    const outside = view.radical.coefficient;
    const hasOutside = !(outside.num === BIG_ONE && outside.den === BIG_ONE);
    if (hasOutside) {
      lines.push(
        t(view.index === 2 ? words.pullOutSquare : words.pullOutIndex, {
          n: view.index,
          outside: `${outside.num}${outside.den === BIG_ONE ? "" : `/${outside.den}`}`,
          inside: view.radical.radicand.toString(),
        })
      );
    } else {
      lines.push(t(words.nothingOut, { x: abs, sign }));
    }
    lines.push(
      t(words.radicalLine, { sign, x: abs, radical: view.radicalText })
    );
  }
  return lines;
}
